<template>
  <div v-if="toolboxGroup">
    <IconFormula size="24" style="padding:10px;"/>
    <p>Model Data</p>
  </div>

  <template v-else>
    <Dialog v-model:visible="openDialog" modal :style="{ width: '50rem' }" :closable="false">
      <template #header>
        <TabView style="width: 100%">
          <TabPanel header="Photo Browser">
            <FileManager @fileChosen="fileChosen"/>
          </TabPanel>
          <TabPanel header="Search Stock Images">
            <StockImageElement :type="`image`" @fileChosen="fileChosen"/>
          </TabPanel>
          <TabPanel header="Search Stock Icons">
            <StockImageElement :type="`icon`" @fileChosen="fileChosen" @filterCss="filterCss"/>
          </TabPanel>
        </TabView>
      </template>

      <template #footer>
        <Button label="Cancel" severity="secondary" @click="openDialog = false"/>
      </template>
    </Dialog>
    <div class="set" :data-hook-id="hookId">
      <div @dblclick="openDialog = !openDialog" :style="elementStyle" v-if="showIcon && !iconAfter">
        <div v-if="fetchingIcons" class="spinner-container">
          <Skeleton :style="getIconStyle()" borderRadius="4px"/>
        </div>
        <div v-else-if="elementImageUrl" >
          <img :style="{ ...getIconStyle(), ...filterStyle }" :src="elementImageUrl"  alt="img"/>
        </div>

        <div v-else>
          <img
              :style="{ ...getIconStyle(), ...filterStyle }"
              src="https://img.freepik.com/premium-vector/default-image-icon-vector-missing-picture-page-website-design-mobile-app-no-photo-available_87543-11093.jpg"
              alt="img"/>
        </div>
      </div>
      <component v-if="currentComponent" :is="currentComponent" :elementData="elementData" :style="style" :viewer="viewer" />

      <div v-if="showIcon && iconAfter" @dblclick="openDialog = !openDialog">
        <div v-if="fetchingIcons" class="spinner-container">
          <Skeleton width="50px" height="50px" borderRadius="4px"/>
        </div>
        <div v-else-if="elementImageUrl">
          <img :style="{ ...getIconStyle(), ...filterStyle }" :src="elementImageUrl" alt="img"/>
        </div>

        <div v-else>
          <img
              :style="{ ...getIconStyle(), ...filterStyle }"
              src="https://img.freepik.com/premium-vector/default-image-icon-vector-missing-picture-page-website-design-mobile-app-no-photo-available_87543-11093.jpg"
              alt="img"/>
        </div>
      </div>

    </div>
  </template>
</template>


<script setup lang="ts">
import OutputTextWidget from "@/views/components/element/DragDropElement/components/OutputTextWidget.vue";
import ChoiceWidget from "@/views/components/element/DragDropElement/components/ChoiceWidget.vue";
import InputNumberWidget from "@/views/components/element/DragDropElement/components/InputNumberWidget.vue";
import ModelTextWidget from "@/views/components/element/DragDropElement/components/ModelTextWidget.vue";
import {computed, getCurrentInstance, onMounted, ref, toRefs, watch} from "vue";
import {useWidgetStore} from "@/store";
import {storeToRefs} from "pinia";
import FileManager from "@/views/components/element/FileManagerElement/index.vue";
import StockImageElement from "@/views/components/element/StockImageElement/index.vue";

const widgetStore = useWidgetStore()

const {activeSubtype, selectedWidget} = storeToRefs(widgetStore)
const toolboxGroup = computed(() => {
  const parentClassList = getCurrentInstance().parent.attrs.class;
  return parentClassList === "dragArea toolbox-group"
});

const props = defineProps({
  elementData: {
    type: Object
  },
  viewer: {
    type: Boolean,
    default: false
  },
  style: {
    type: Object
  },
  class: String
})

const {elementData} = toRefs(props)

const elementSubtype = computed(() => elementData.value?.subType);
const openDialog = ref(false)
const elementImageUrl = computed(() => elementData.value?.imageUrl ?? null)
const fetchingIcons = computed(() => elementData.value.fetchingIcons)

const elementStyle = computed(() => {
  const style = elementData.value?.style ?? {};
  const {filter, ...withoutFilter} = style;
  return withoutFilter;
});

const filterStyle = computed(() => {
  const style = elementData.value?.style ?? {};
  return {
    filter: style.filter
  };
});


const positionFlex = computed(() => elementData.value?.iconPosition === 'none' || elementData.value?.iconPosition === 'left' || elementData.value?.iconPosition === 'right' ? 'row' : 'column')
const showIcon = computed(() => elementData.value?.iconPosition !== 'none')
const iconAfter = computed(() => elementData.value?.iconPosition === 'bottom' || elementData.value?.iconPosition === 'right')
const iconMargins = computed(() => elementData.value?.iconPosition === 'left' || elementData.value?.labelPosition === 'right' ? '0 10px' : '10px 0')
const hookId = computed(() => selectedWidget.value.hookId)
const elementText = computed(() => selectedWidget.value.text)


const getIconStyle = () => {
  return {
    maxWidth: '100%',
    maxHeight: '100%',
    width: px(elementData.value.style?.iconWidth, '100px'),
    height: px(elementData.value.style?.iconHeight, '100px'),
  }
}

const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  } else {
    return defaultValue
  }
}

const widgetMapping = {
  'out_text': OutputTextWidget,
  'choice': ChoiceWidget,
  'input_number': InputNumberWidget,
  'model_text': ModelTextWidget,
};

const fileChosen = (file: any) => {

  if (file.resultsThumbnailImg) {
    elementData.value.style.filter = ''
    elementData.value.imageUrl = file.resultsThumbnailImg
  } else {
    elementData.value.style.filter = ''
    selectedWidget.value.imageUrl = file.url;
  }

  openDialog.value = false
}

const filterCss = (obj: any) => {
  elementData.value.style.filter = obj.filter
  elementData.value.style.hex = obj.hex
}

const currentComponent = computed(() => {
  return widgetMapping[elementSubtype.value]
});

watch(elementSubtype, (newVal) => {
  activeSubtype.value = newVal
})



const fetchAiIcons = async (elementText: string) => {
  selectedWidget.value.fetchingIcons = true;

  const response = await fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/file-service/ai-selected-icons`, {
    method: 'POST',
    body: JSON.stringify({variableName: elementText})
  })

  const data = await response.json()

  if (data.icons && data.icons.length > 0) {
    selectedWidget.value.imageUrl = data.icons[0];
    selectedWidget.value.icons = data.icons
    selectedWidget.value.iconNouns = data.nouns
  } else {
    selectedWidget.value.imageUrl = "https://via.placeholder.com/150?text=No+Results";
  }
}

const combinedValue = computed(() => {
  return { hookId: hookId.value, selectedWidget: selectedWidget.value.id }
})

watch(combinedValue, async (newValues, oldValues) => {
  const { hookId: newHookId, selectedWidget: newSelectedWidget } = newValues
  const { hookId: oldHookId, selectedWidget: oldSelectedWidget } = oldValues

  if (newSelectedWidget === oldSelectedWidget) {
    if (newHookId !== oldHookId) {
      await fetchAiIcons(elementText.value)
      selectedWidget.value.fetchingIcons = false;
    }
  }

}, {deep: true})

</script>

<style scoped lang="scss">
.set {
  display: flex;
  flex-direction: v-bind(positionFlex);
  align-items: center;
}

img {
  margin: v-bind(iconMargins);
}


:deep(.p-tabview-panels) {
  overflow: auto;
  max-height: 400px;
}

</style>
